/**
 * use-composer-tools.ts — Tiện ích ghim ở thanh công cụ ô soạn tin trên điện thoại, LƯU THEO TÀI KHOẢN và đồng bộ giữa các thiết bị.
 *
 *   GET/PUT /me/ui-prefs/composer-tools   { tools, updatedAt }   (backend: modules/me/ui-prefs*.ts, AppSetting `ui.composer-tools.<userId>`)
 *
 * Cùng cơ chế với use-mobile-segments.ts:
 * - localStorage `ecdp_composer_tools:<userId>` là bộ nhớ đệm — hiện NGAY khi mở, không chớp chờ mạng; mỗi người dùng một khoá nên hai tài khoản
 *   dùng chung một máy không lẫn.
 * - Mở / quay lại tab: lấy bản máy chủ và hợp nhất (`mergeToolsPref`): bản `updatedAt` mới hơn thắng; máy chủ chưa có mà máy này có → đẩy lên.
 * - Sửa: áp ngay + lưu đệm + PUT nền sau ~500ms (gộp nhiều lần sửa; rời màn thì gửi luôn). Lỗi mạng → giữ bản cục bộ, KHÔNG báo lỗi.
 * - Xem dưới quyền người khác: chỉ đọc, không ghi (backend cũng chặn).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import {
  DEFAULT_PINNED, loadToolsPref, mergeToolsPref, normalizeTools, parseToolsPref, saveToolsPref, type ComposerToolId, type ToolsPref,
} from '@/lib/composer-tools'

const URL = '/me/ui-prefs/composer-tools'
export const TOOLS_PUSH_DEBOUNCE_MS = 500

export function useComposerTools(userId: string | undefined, opts: { readOnly?: boolean } = {}) {
  const { readOnly } = opts
  const qc = useQueryClient()
  const [pref, setPref] = useState<ToolsPref | null>(() => loadToolsPref(userId))
  const prefRef = useRef(pref)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dirty = useRef<ToolsPref | null>(null)
  const queryKey = useMemo(() => ['ui-prefs', 'composer-tools', userId] as const, [userId])

  const apply = useCallback((next: ToolsPref | null) => { prefRef.current = next; setPref(next) }, [])

  // Đổi người dùng (đăng nhập lại, xem dưới quyền) → nạp bộ nhớ đệm của người đó.
  useEffect(() => { apply(loadToolsPref(userId)) }, [userId, apply])

  const push = useCallback(async (p: ToolsPref) => {
    if (readOnly || !userId) return
    try {
      const { data } = await api.put<{ pref: ToolsPref; applied: boolean }>(URL, p)
      qc.setQueryData(queryKey, { pref: data.pref })
      // Máy chủ đang giữ bản mới hơn (thiết bị khác vừa sửa) → nhận bản đó.
      if (!data.applied) {
        const server = parseToolsPref(data.pref)
        if (server) { saveToolsPref(userId, server); apply(server) }
      }
    } catch { /* mạng lỗi / 403 → giữ bản cục bộ, lần hợp nhất sau tự thử lại */ }
  }, [readOnly, userId, qc, queryKey, apply])

  const flush = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null }
    const p = dirty.current
    dirty.current = null
    if (p) void push(p)
  }, [push])

  const schedule = useCallback((p: ToolsPref) => {
    dirty.current = p
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(flush, TOOLS_PUSH_DEBOUNCE_MS)
  }, [flush])

  // Rời màn chat khi còn bản chờ gửi → gửi luôn.
  useEffect(() => () => flush(), [flush])

  const remote = useQuery<{ pref: ToolsPref | null }>({
    queryKey,
    enabled: !!userId,
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    queryFn: async () => {
      const { data } = await api.get<{ pref: unknown }>(URL)
      return { pref: parseToolsPref(data.pref) }
    },
  })

  // Có bản máy chủ (kể cả "chưa có") → hợp nhất. Đọc bản cục bộ MỚI NHẤT qua ref (người dùng có thể vừa sửa trong lúc chờ mạng).
  useEffect(() => {
    if (!remote.data || !userId) return
    const { pref: merged, action } = mergeToolsPref(prefRef.current, remote.data.pref)
    if (action === 'adopt-remote' && merged) { saveToolsPref(userId, merged); apply(merged) }
    else if (action === 'push-local' && merged) schedule(merged)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remote.dataUpdatedAt, userId])

  /** Người dùng sửa: áp ngay + lưu đệm + đẩy nền. */
  const setTools = useCallback((tools: ComposerToolId[]) => {
    const next: ToolsPref = { tools: normalizeTools(tools), updatedAt: new Date().toISOString() }
    saveToolsPref(userId, next)
    apply(next)
    schedule(next)
  }, [userId, apply, schedule])

  const tools = useMemo(() => normalizeTools(pref?.tools ?? DEFAULT_PINNED), [pref])
  return { tools, setTools }
}
